import type { IDataFeedProvider } from '../dataFeed/IDataFeedProvider.js';
import { ITradingProvider } from '@ocktra/shared-types';
import type { DataFeedProviderType, TradingProviderType } from './types';

import { ZerodhaDataFeedProvider } from '../dataFeed/ZerodhaDataFeedProvider.js';
import { KiteTradingProvider } from './kite/TradingProvider.js';
import { PaperTradingProvider } from './papertrade/TradingProvider.js';

/**
 * Factory for creating provider instances based on configuration
 * This allows easy swapping between different data feed and trading providers
 * Supports multiple instances of both data feed and trading providers
 */
export class ProviderFactory {
    private static dataFeedInstances: Map<DataFeedProviderType, IDataFeedProvider> = new Map();
    private static tradingProviderInstances: Map<TradingProviderType, ITradingProvider> = new Map();

    /**
     * Get or create a data feed provider instance (without connecting)
     */
    static getDataFeedProvider(type: DataFeedProviderType): IDataFeedProvider | null {
        // If instance exists for this type, return it
        if (this.dataFeedInstances.has(type)) {
            return this.dataFeedInstances.get(type)!;
        }

        // Create new instance based on type
        let instance: IDataFeedProvider;

        switch (type) {
            case 'kite':
                instance = new ZerodhaDataFeedProvider();
                break;

            // TODO: Implement real providers
            // case 'polygon':
            //     instance = new PolygonDataFeedProvider();
            //     break;

            default:
                console.warn(`[ProviderFactory] Unknown data feed provider type: ${type}`);
                return null;
        }

        this.dataFeedInstances.set(type, instance);
        return instance;
    }


    /**
     * Get or create a trading provider instance (without connecting)
     */
    static getTradingProvider(type: TradingProviderType): ITradingProvider | null {
        // If instance exists for this type, return it
        if (this.tradingProviderInstances.has(type)) {
            return this.tradingProviderInstances.get(type)!;
        }

        // Create new instance based on type
        let instance: ITradingProvider;

        switch (type) {

            case 'kite':
                // Will be configured with credentials during connect
                instance = new KiteTradingProvider();
                break;

            case 'papertrade':
                instance = new PaperTradingProvider();
                break;

            default:
                console.warn(`[ProviderFactory] Unknown trading provider type: ${type}`);
                return null;
        }

        this.tradingProviderInstances.set(type, instance);
        return instance;
    }
}

/**
 * Helper to get default provider config for a broker
 * Maps broker names to appropriate provider types
 */

